import { chronicleIsInputAtom } from '@/atoms/chronicle';
import {
  activeSessionIdAtom,
  type PendingRecordingAction,
  pendingRecordingActionAtom,
} from '@/atoms/liveTranscriptionAtom';
import { cn } from '@/lib/cn';
import { type RootState } from '@/store';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@heidi/design-system/hover-card';
import { useTranslations } from '@heidi/runtime/i18n';
import { RecordingStatus, TranscribeMode } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DefaultSessionTile } from './default-tile';
import { ChronicleSessionTileContent, VoiceIndicator } from './shared';
import { useIndicatorAnalytics } from './useIndicatorAnalytics';

interface LiveTranscriptionIndicatorProps {
  readonly isSidebarOpen: boolean;
}

export function LiveTranscriptionIndicator({
  isSidebarOpen,
}: LiveTranscriptionIndicatorProps) {
  const { t } = useTranslations();
  const router = useRouter();
  const activeSessionId = useAtomValue(activeSessionIdAtom);
  const setPendingAction = useSetAtom(pendingRecordingActionAtom);
  const isChronicleInput = useAtomValue(chronicleIsInputAtom);

  const routeId = router.query.id;
  const currentPageSessionId = Array.isArray(routeId) ? routeId[0] : routeId;
  const isOnRecordingSessionPage =
    router.pathname.startsWith('/scribe/session') &&
    currentPageSessionId === activeSessionId;

  const { data: session } = useQuerySessionById({
    sessionId: activeSessionId ?? '',
    select: (data) =>
      data
        ? {
            recording_status: data.recording_status,
            transcribe_mode: data.transcribe_mode,
            patient_profile: data.patient_profile,
            patient: data.patient,
            session_name: data.session_name,
          }
        : undefined,
  });

  const isRunning = useSelector((state: RootState) => state.timerModel.isRunning);
  const seconds = useSelector((state: RootState) => state.timerModel.seconds);
  const isRecording =
    session?.recording_status === RecordingStatus.RECORDING || isRunning;
  const isPaused = session?.recording_status === RecordingStatus.PAUSED && !isRunning;

  const displayName = useMemo(() => {
    const name =
      session?.patient_profile?.demographic_string ||
      session?.patient?.demographic_details ||
      session?.patient?.name;
    return name || session?.session_name || t('liveTranscription.activeSession');
  }, [session, t]);

  const { trackIndicatorClicked, trackControlClicked } = useIndicatorAnalytics({
    sessionId: activeSessionId ?? '',
    isSidebarOpen,
  });

  const navigateToSession = useCallback(() => {
    if (!activeSessionId || isOnRecordingSessionPage) return;
    trackIndicatorClicked();
    void router.push(`/scribe/session/${activeSessionId}`);
  }, [activeSessionId, isOnRecordingSessionPage, router, trackIndicatorClicked]);

  const queuePendingAction = useCallback(
    (action: PendingRecordingAction['action']) => {
      if (!activeSessionId) return;
      trackControlClicked(action);
      setPendingAction({
        action,
        sessionId: activeSessionId,
        requestedAt: Date.now(),
        source: 'live-transcription-indicator',
      });
      if (!isOnRecordingSessionPage) {
        void router.push(`/scribe/session/${activeSessionId}`);
      }
    },
    [
      activeSessionId,
      isOnRecordingSessionPage,
      router,
      setPendingAction,
      trackControlClicked,
    ]
  );

  const handlePauseOrResumeClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPaused) {
        queuePendingAction('resume');
      } else if (isRecording) {
        queuePendingAction('pause');
      }
    },
    [isPaused, isRecording, queuePendingAction]
  );

  const handleStopClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      queuePendingAction('stop');
    },
    [queuePendingAction]
  );

  if (!activeSessionId || !session) return null;
  // Only Chronicle keeps the indicator visible while paused — device-side
  // pause/resume happens off the session page. Web/desktop-mic hides on pause.
  const isActive = isChronicleInput ? isRecording || isPaused : isRecording;
  if (!isActive) return null;

  const isDictating = session.transcribe_mode === TranscribeMode.DICTATE;
  const pauseLabel = isDictating
    ? t('tooltips.pauseDictating')
    : t('tooltips.pauseTranscribing');
  const resumeLabel = isDictating
    ? t('sessionHeader.resumeDictating')
    : t('sessionHeader.resumeTranscribing');
  const stopLabel = isDictating
    ? t('tooltips.stopDictating')
    : t('tooltips.stopTranscribing');
  const returnLabel = t('liveTranscription.returnToSession');

  // ── Chronicle branch — always show pause/stop regardless of page ──
  if (isChronicleInput) {
    if (!isSidebarOpen) {
      return (
        <div className="mt-auto flex w-full justify-center px-1 pb-2">
          <HoverCard openDelay={100} closeDelay={200}>
            <HoverCardTrigger asChild>
              <button
                onClick={navigateToSession}
                className={cn(
                  'flex size-8 items-center justify-center rounded-md',
                  'bg-surface-accent transition-colors duration-200 hover:bg-surface-accent-hover'
                )}
                aria-label={returnLabel}
              >
                <VoiceIndicator isRecording={isRecording} />
              </button>
            </HoverCardTrigger>
            <HoverCardContent
              side="right"
              align="end"
              sideOffset={8}
              className="w-auto min-w-[200px] border-0 bg-surface-accent p-0"
            >
              {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- avoids nesting <button> */}
              <div
                role="button"
                tabIndex={0}
                onClick={navigateToSession}
                onKeyDown={(e) =>
                  e.target === e.currentTarget &&
                  (e.key === 'Enter' || e.key === ' ') &&
                  navigateToSession()
                }
                className="cursor-pointer rounded-[inherit] bg-surface-accent p-3 transition-colors hover:bg-surface-accent-hover"
              >
                <ChronicleSessionTileContent
                  displayName={displayName}
                  seconds={seconds}
                  isPaused={isPaused}
                  pauseLabel={pauseLabel}
                  resumeLabel={resumeLabel}
                  stopLabel={stopLabel}
                  onPauseOrResume={handlePauseOrResumeClick}
                  onStop={handleStopClick}
                />
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
      );
    }

    return (
      <div className="mt-auto px-2 pb-2">
        {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- avoids nesting <button> */}
        <div
          role="button"
          tabIndex={0}
          onClick={navigateToSession}
          onKeyDown={(e) =>
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ') &&
            navigateToSession()
          }
          className={cn(
            'w-full rounded-md bg-surface-accent p-3',
            'cursor-pointer transition-colors duration-200 ease-out hover:bg-surface-accent-hover'
          )}
          aria-label={returnLabel}
        >
          <ChronicleSessionTileContent
            displayName={displayName}
            seconds={seconds}
            isPaused={isPaused}
            pauseLabel={pauseLabel}
            resumeLabel={resumeLabel}
            stopLabel={stopLabel}
            onPauseOrResume={handlePauseOrResumeClick}
            onStop={handleStopClick}
          />
        </div>
      </div>
    );
  }

  // ── Default branch — original tile for web / desktop mic/speaker ──
  return (
    <DefaultSessionTile
      isSidebarOpen={isSidebarOpen}
      isRecording={isRecording}
      seconds={seconds}
      displayName={displayName}
      showControls={isOnRecordingSessionPage}
      pauseLabel={pauseLabel}
      stopLabel={stopLabel}
      returnLabel={returnLabel}
      onNavigate={navigateToSession}
      onPause={handlePauseOrResumeClick}
      onStop={handleStopClick}
    />
  );
}
